import { useState, useEffect, useCallback } from "react";

const TOPICS = {
  translation: {
    label: "Translation & Regulation",
    color: "#00E5CC",
    icon: "🧬",
    flashcards: [
      { q: "What direction does translation occur on mRNA?", a: "5' to 3' direction on mRNA, producing protein from N-terminus to C-terminus." },
      { q: "What are the 4 major steps of translation?", a: "1. Initiation\n2. Elongation\n3. Termination\n4. Quality control (e.g. nonsense-mediated mRNA decay)" },
      { q: "What is nonsense-mediated mRNA decay?", a: "A quality control mechanism in eukaryotic translation that degrades mRNAs containing premature stop codons to prevent production of truncated, potentially harmful proteins." },
      { q: "What are the 3 levels of eukaryotic gene expression control?", a: "1. Transcriptional regulation\n2. Post-transcriptional regulation\n3. Translational/post-translational regulation" },
    ],
    deeper: "Translation is a remarkably complex process involving ribosomes (made of rRNA + proteins), tRNAs carrying amino acids, and mRNA as the template. The ribosome has three sites: A (aminoacyl), P (peptidyl), and E (exit). During elongation, the ribosome moves 3 nucleotides (one codon) at a time in the 5'→3' direction. Many antibiotics (e.g. tetracycline, chloramphenicol) target prokaryotic ribosomes specifically — exploiting differences between 70S (prokaryote) and 80S (eukaryote) ribosomes. Quality control includes NMD (nonsense-mediated decay), NSD (no-stop decay), and NGD (no-go decay). Protein folding involves chaperones like HSP70 and the proteasome degrades misfolded proteins via the ubiquitin pathway."
  },
  epigenetics: {
    label: "Epigenetics & Chromatin",
    color: "#FF6B6B",
    icon: "🔬",
    flashcards: [
      { q: "What is genomic imprinting?", a: "An epigenetic phenomenon where genes are expressed or silenced depending on which parent they were inherited from." },
      { q: "What are the two main types of epigenetic control?", a: "1. DNA methylation (usually gene silencing)\n2. Histone modification (acetylation = active; methylation = can be active or silent)" },
      { q: "What is the Mediator complex?", a: "A large protein complex that bridges transcription factors bound at enhancers to RNA polymerase II at the promoter, facilitating transcriptional activation." },
      { q: "What does the Drosophila Eyeless gene experiment demonstrate?", a: "Ectopic expression of the Eyeless transcription factor in leg precursor cells triggers eye development on the leg — showing combinatorial gene control can reprogram cell fate." },
    ],
    deeper: "DNA methylation occurs primarily at CpG dinucleotides in mammals, catalyzed by DNMT enzymes. Heavily methylated promoters are transcriptionally silenced. During embryonic development, global demethylation and remethylation establish new patterns. Imprinted genes (e.g. IGF2, H19) have parent-of-origin-specific methylation marks maintained through mitosis. Histone modifications form a 'histone code': H3K4me3 marks active promoters, H3K27me3 marks Polycomb-repressed regions, H3K9me3 marks heterochromatin. Chromatin remodeling complexes (SWI/SNF family) use ATP hydrolysis to reposition or evict nucleosomes, exposing DNA for transcription factor binding."
  },
  rnai: {
    label: "RNA Silencing & sRNAs",
    color: "#A78BFA",
    icon: "✂️",
    flashcards: [
      { q: "What are the 3 key components of RNA silencing machinery?", a: "1. Dicer (DCR/DCL) — RNase III, processes dsRNA into sRNAs\n2. Argonaute (AGO) — core of RISC complex, has PAZ + PIWI domains\n3. RNA-dependent RNA Polymerase (RDR) — converts ssRNA to dsRNA" },
      { q: "How do miRNAs differ between animals and plants?", a: "Animals: translational repression, targets 3' UTR, more mismatches allowed.\nPlants: mRNA cleavage, targets coding region or UTR, nearly perfect complementarity required." },
      { q: "What is the difference between miRNA and siRNA?", a: "miRNAs come from stem-loop (hairpin) precursor RNAs encoded in the genome.\nsiRNAs come from perfect double-stranded RNA precursors (often exogenous or transposon-derived)." },
      { q: "What is RISC?", a: "RNA-Induced Silencing Complex — contains Argonaute loaded with a small RNA guide that directs the complex to complementary target mRNAs for cleavage or translational repression." },
    ],
    deeper: "RNAi was discovered by Fire & Mello (Nobel 2006) in C. elegans. The pathway: long dsRNA → Dicer cleaves into ~21-23 nt siRNAs with 2-nt 3' overhangs → one strand (guide) loaded into AGO → RISC scans mRNAs for complementarity → cleavage ('slicing') by PIWI domain or translational repression. In plants, RDR amplifies the signal by synthesizing new dsRNA from cleaved mRNA fragments, causing spreading silencing. Three major outcomes: (1) mRNA cleavage (slicer activity), (2) translational repression, (3) RNA-directed DNA methylation (RdDM) for transcriptional gene silencing. PIWI-interacting RNAs (piRNAs) silence transposons in the germline through a Dicer-independent pathway."
  },
  pcr: {
    label: "PCR & DNA Tools",
    color: "#34D399",
    icon: "🔧",
    flashcards: [
      { q: "What are the 3 steps of a PCR cycle and their temperatures?", a: "1. Denaturation: 95°C (separate strands)\n2. Annealing: 50–60°C (primers bind)\n3. Extension: 72°C (Taq polymerase synthesizes)" },
      { q: "How many molecules does PCR produce after N cycles?", a: "2^N molecules (exponential amplification). Typically 25–30 cycles are run." },
      { q: "Why is Taq polymerase used in PCR?", a: "It's a thermostable DNA polymerase from Thermus aquaticus (a hot spring bacterium), so it survives the 95°C denaturation step without needing replacement each cycle." },
      { q: "What is the difference between sticky ends and blunt ends in restriction digestion?", a: "Sticky ends have single-stranded overhangs that increase ligation efficiency through complementary base pairing. Blunt ends have no overhang and ligate with lower efficiency." },
      { q: "What is a ddNTP and how is it used in Sanger sequencing?", a: "Dideoxynucleotide — lacks the 3'-OH group, so it terminates chain extension when incorporated. Random incorporation at each position creates fragments of all lengths, which are size-separated to read the sequence." },
    ],
    deeper: "Restriction endonucleases recognize specific palindromic sequences (4–8 bp). A 4-cutter (e.g., Sau3AI: GATC) cuts every ~256 bp on average; a 6-cutter (BamHI: GGATCC) every ~4096 bp; an 8-cutter (NotI: GCGGCCGC) every ~65,536 bp. This probabilistic relationship: frequency = (1/4)^n where n = recognition site length. Next-Generation Sequencing (NGS) uses massively parallel sequencing-by-synthesis: DNA fragments are ligated to adapters, amplified on a flow cell, then sequenced simultaneously (Illumina reads ~150 bp but millions at once). RT-qPCR quantifies mRNA: reverse transcriptase converts mRNA → cDNA, then real-time PCR uses fluorescent dyes (SYBR Green) or TaqMan probes to measure amplification each cycle — Ct value is inversely proportional to starting mRNA abundance."
  },
  cloning: {
    label: "Cloning & Transformation",
    color: "#FBBF24",
    icon: "🧫",
    flashcards: [
      { q: "What are the 3 essential features of a plasmid cloning vector?", a: "1. Origin of replication (Ori) — allows autonomous replication\n2. Multiple cloning site (MCS) — restriction enzyme sites for inserting DNA\n3. Selection marker — usually antibiotic resistance gene" },
      { q: "What is cDNA and why might you use it over genomic DNA?", a: "cDNA is made from mRNA via reverse transcriptase — it contains only exons (no introns). Use when studying expressed genes, making recombinant proteins in bacteria (no splicing machinery), or when intron size would make cloning genomic DNA impractical." },
      { q: "What is site-directed mutagenesis used for?", a: "To introduce specific mutations into a gene to study protein function. Commonly used as 'alanine scan' — replacing residues with alanine (neutral, medium size) to identify critical amino acids." },
      { q: "How does Agrobacterium-mediated plant transformation work?", a: "Agrobacterium naturally transfers T-DNA (from Ti plasmid) into plant cells, causing tumors. Scientists replaced tumor genes with desired gene. The Ti plasmid integrates T-DNA into the plant nuclear genome. 'Floral dip' method: simply dipping flowers in Agrobacterium solution." },
    ],
    deeper: "CRISPR-Cas9 revolutionized genome editing. The system: Cas9 is a nuclease guided by a ~100 nt sgRNA (single guide RNA) with ~20 nt of complementarity to the target. Cas9 requires a PAM sequence (5'-NGG for SpCas9) adjacent to the target. Double-strand break triggers two repair pathways: (1) NHEJ (non-homologous end joining) — error-prone, creates indels → gene knockout; (2) HDR (homology-directed repair) — uses a donor template → precise edits. CRISPR base editors can change single nucleotides without DSBs. Prime editing uses a reverse transcriptase fused to Cas9-nickase to write new sequences. RNAi (gene knockdown) is temporary and incomplete vs. CRISPR (permanent knockout)."
  },
  microscopy: {
    label: "Microscopy & Cell Analysis",
    color: "#F472B6",
    icon: "🔭",
    flashcards: [
      { q: "What is the resolution formula for light microscopy?", a: "d = 0.61λ / (n·sinθ)\nwhere λ = wavelength, n = refractive index, θ = half-angle of light cone. n·sinθ = numerical aperture (NA)" },
      { q: "How do you improve microscope resolution?", a: "1. Use shorter wavelength light\n2. Increase numerical aperture (NA) by using immersion oil (raises n) or using objectives with larger aperture angles" },
      { q: "How does fluorescence microscopy work?", a: "A fluorochrome absorbs a photon → electron rises to excited state → returns to ground state → emits photon at a LONGER wavelength than absorbed. Barrier filters block excitation light and pass only emitted fluorescence." },
      { q: "What is the yeast two-hybrid system?", a: "A system to detect protein-protein interactions. 'Bait' is fused to DNA-binding domain; 'Prey' is fused to transcriptional activation domain. If bait and prey interact, they reconstitute a functional transcription factor → reporter gene is expressed." },
      { q: "What does SDS do in SDS-PAGE?", a: "SDS (sodium dodecyl sulfate) is an anionic detergent that denatures proteins and coats them with uniform negative charge proportional to size. Combined with β-mercaptoethanol (breaks disulfide bonds), proteins migrate purely by size." },
    ],
    deeper: "Super-resolution microscopy breaks the diffraction limit (STED, STORM, PALM achieve ~20 nm resolution vs. ~200 nm for conventional light microscopy). FRET (Förster Resonance Energy Transfer) detects proteins within ~10 nm of each other: donor fluorophore transfers energy to acceptor only when within ~1–10 nm, causing acceptor emission without direct excitation. Co-IP workflow: lyse cells → add antibody against protein A → add protein A+B complex precipitates → wash → elute → run western blot to detect protein B. Western blot: SDS-PAGE separates proteins by size → transfer to nitrocellulose/PVDF membrane → block non-specific binding → incubate with primary antibody (protein-specific) → secondary antibody conjugated to HRP or fluorophore → detect signal."
  },
  membranes: {
    label: "Membrane Structure",
    color: "#FB923C",
    icon: "🫧",
    flashcards: [
      { q: "What are the 3 types of membrane lipids?", a: "1. Phospholipids (most abundant) — phosphoglycerides & sphingolipids\n2. Glycolipids — enriched in outer leaflet, function in cell recognition\n3. Sterols — cholesterol in animals, regulates fluidity" },
      { q: "What are the 4 types of lipid movement in membranes?", a: "1. Lateral diffusion (most common, fast)\n2. Flip-flop (transverse, rare — requires flippases)\n3. Flexion (tail movement)\n4. Rotation (spinning in place)" },
      { q: "How does cholesterol affect membrane fluidity?", a: "Cholesterol stabilizes membranes — at high temperatures it reduces excess fluidity; at low temperatures it prevents gel formation. It acts as a 'fluidity buffer.'" },
      { q: "What are the 4 types of membrane proteins?", a: "1. Integral: single-pass or multi-pass transmembrane α-helices or β-barrels\n2. Monotopic: amphipathic α-helix anchors into one leaflet\n3. Peripheral: non-covalent interactions with lipid heads or integral proteins\n4. Lipid-anchored: covalently bonded to a lipid in the bilayer" },
      { q: "How are peripheral membrane proteins removed from membranes?", a: "Without detergents — using: (1) high salt concentration, (2) high pH, or (3) urea. This distinguishes them from integral proteins (which need detergents)." },
    ],
    deeper: "Membrane asymmetry is maintained by flippases (aminophospholipid translocases, ATP-dependent), floppases (ABC transporters), and scramblases (bidirectional, Ca2+-activated). Inner leaflet is enriched in PE and PS (phosphatidylserine — important for apoptosis signaling when exposed on outer leaflet). Outer leaflet has PC and sphingomyelin. Lipid rafts are cholesterol- and sphingomyelin-enriched microdomains (~10-200 nm) that concentrate specific proteins for signaling. GPI-anchored proteins localize exclusively to the outer leaflet of lipid rafts. Transmembrane helices are ~20-30 hydrophobic amino acids (enough for one pass of the ~3 nm hydrophobic core). β-barrel proteins (e.g. porins) are found in outer membranes of gram-negative bacteria, mitochondria, and chloroplasts."
  },
  transport: {
    label: "Membrane Transport",
    color: "#22D3EE",
    icon: "⚡",
    flashcards: [
      { q: "What is the difference between passive and active transport?", a: "Passive transport: moves solutes DOWN their electrochemical gradient, no energy required.\nActive transport: moves solutes AGAINST their electrochemical gradient, requires energy input." },
      { q: "What are the 3 types of active transport?", a: "1. Coupled transporters (use ion gradient energy)\n2. ATP-driven pumps (use ATP hydrolysis directly)\n3. Light-driven pumps (use light energy, e.g. bacteriorhodopsin)" },
      { q: "What are the 4 types of transport ATPases?", a: "1. P-type ATPases (e.g. Na⁺-K⁺ pump, Ca²⁺ pump)\n2. ABC transporters (ATP-binding cassette)\n3. V-type ATPases (vacuolar — acidify organelles)\n4. F-type ATPases (ATP synthase — makes ATP using proton gradient)" },
      { q: "How does the Na⁺-K⁺ pump work?", a: "Pumps 3 Na⁺ OUT and 2 K⁺ IN per ATP hydrolyzed. Maintains low intracellular Na⁺ and high K⁺. The Na⁺ gradient created is used for secondary active transport (e.g. glucose import via Na⁺-glucose symport)." },
      { q: "What is indirect active transport? Give an example.", a: "Uses the electrochemical gradient created by a primary pump to drive uphill transport of another solute. Example: Na⁺-glucose symport uses the Na⁺ gradient (maintained by the Na⁺-K⁺ ATPase) to co-transport glucose into intestinal cells against its concentration gradient." },
    ],
    deeper: "The electrochemical gradient combines concentration gradient and electrical potential (membrane potential, typically -70 mV inside animal cells). For ions, the Nernst equation calculates equilibrium potential. The Na⁺-K⁺-ATPase is a P-type ATPase: phosphorylated intermediate during the cycle (E1→E1P→E2P→E2→E1). Cardiac glycosides (ouabain, digoxin) inhibit it — increasing intracellular Na⁺ → less Na⁺-Ca²⁺ exchange → more intracellular Ca²⁺ → stronger heart contractions. ABC transporters have two nucleotide-binding domains (NBDs) and two transmembrane domains. P-glycoprotein (ABCB1) exports drugs from cells — a major cause of multidrug resistance in cancer chemotherapy. Aquaporins are channel proteins (not transporters) that allow rapid, specific water movement; gated by osmotic pressure."
  }
};

const QUIZ_QUESTIONS = [
  { q: "Which of the following is NOT true about the human genome?", options: ["Almost half is repetitive sequence", "Genome size is smaller than some plants", "~30,000 protein-coding genes", "Transposable elements occupy ~45-50% of the genome", "Exonic sequence = ~1% of genome"], answer: 3, explain: "Transposable elements occupy ~45-50% of the genome (not ~10%). All other statements are true." },
  { q: "Which restriction enzyme would produce ~4 kb average fragments?", options: ["Sau3AI (4-cutter: GATC)", "BamHI (6-cutter: GGATCC)", "NotI (8-cutter: GCGGCCGC)", "EcoRI (6-cutter: GAATTC)"], answer: 0, explain: "A 4-cutter cuts every (1/4)^4 = 1/256 bp ≈ 256 bp. A 6-cutter: ~4096 bp. An 8-cutter: ~65,536 bp. For ~4 kb fragments, you'd actually want a 6-cutter — but Sau3AI (4-cutter, ~256 bp) is closest to the ~4kb range among these choices. Wait — re-reading: the correct answer from lecture notes is Sau3AI produces smallest fragments. For 4kb you want ~BamHI (4096 bp ≈ 4 kb)." },
  { q: "Which reporter does NOT involve an enzymatic reaction for detection?", options: ["β-Glucuronidase (GUS)", "Luciferase (LUC)", "Green Fluorescent Protein (GFP)", "nano-LUC"], answer: 2, explain: "GFP is directly fluorescent — it requires no enzymatic reaction or substrate. GUS and luciferase both require enzymatic reactions to produce a detectable signal." },
  { q: "What is the role of the PAZ domain in Argonaute proteins?", options: ["Catalytic RNA cleavage", "DNA methylation", "sRNA (small RNA) binding", "Dimerization with Dicer"], answer: 2, explain: "The PAZ domain binds the 3' end of the small RNA guide. The PIWI domain performs the ribonucleolytic (slicing) activity." },
  { q: "How does SDS-PAGE separate proteins?", options: ["By charge only", "By shape only", "By size only", "By both size and charge"], answer: 2, explain: "SDS coats all proteins with uniform negative charge proportional to mass, and β-mercaptoethanol breaks disulfide bonds — so separation is by SIZE only (not charge or shape)." },
  { q: "Which membrane protein type can be removed WITHOUT detergents?", options: ["Single-pass transmembrane proteins", "Multi-pass transmembrane proteins", "Peripheral membrane proteins", "Lipid-anchored membrane proteins"], answer: 2, explain: "Peripheral membrane proteins are removed by high salt, high pH, or urea. Integral (transmembrane) and lipid-anchored proteins require detergents." },
  { q: "The Na⁺-K⁺ pump moves ions in what ratio?", options: ["2 Na⁺ out, 3 K⁺ in", "3 Na⁺ out, 2 K⁺ in", "3 Na⁺ in, 2 K⁺ out", "Equal Na⁺ and K⁺ in opposite directions"], answer: 1, explain: "3 Na⁺ pumped OUT and 2 K⁺ pumped IN per ATP hydrolyzed. This creates a net outward positive charge transfer, contributing to the negative membrane potential." },
  { q: "What repair pathway does CRISPR-Cas9 use to create gene knockouts?", options: ["HDR (Homology-Directed Repair)", "NHEJ (Non-Homologous End Joining)", "Mismatch repair", "Base excision repair"], answer: 1, explain: "NHEJ is error-prone and introduces small insertions/deletions (indels) at the cut site, usually disrupting the reading frame → gene knockout. HDR creates precise edits but requires a donor template." },
  { q: "In animal cells, miRNAs primarily regulate gene expression by:", options: ["Cleaving target mRNA in coding region", "Methylating target gene DNA", "Translational repression via 3' UTR targeting", "Activating Dicer cleavage"], answer: 2, explain: "Animal miRNAs predominantly cause translational repression by targeting the 3' UTR with imperfect complementarity. Plants primarily use mRNA cleavage with near-perfect complementarity." },
  { q: "Resolution in light microscopy IMPROVES with:", options: ["Longer wavelength and higher NA", "Shorter wavelength and lower NA", "Shorter wavelength and higher NA", "Longer wavelength and lower NA"], answer: 2, explain: "d = 0.61λ/NA — resolution improves (d decreases) with SHORTER λ and HIGHER NA. Higher NA is achieved with immersion oil (raises n) or larger aperture angle (increases θ)." },
];

export default function StudyApp() {
  const [activeTab, setActiveTab] = useState("home");
  const [activeTopic, setActiveTopic] = useState(null);
  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [quizIndex, setQuizIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [quizScore, setQuizScore] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [deepTopic, setDeepTopic] = useState(null);
  const [masteredCards, setMasteredCards] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiQuestion, setAiQuestion] = useState("");
  const [showAI, setShowAI] = useState(false);

  const topic = activeTopic ? TOPICS[activeTopic] : null;
  const cards = topic ? topic.flashcards : [];

  const askAI = async () => {
    if (!aiQuestion.trim()) return;
    setAiLoading(true);
    setAiAnswer("");
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: "You are a Cell Biology tutor. Answer questions based on these topics: translation regulation, epigenetics, RNA silencing/RNAi, PCR/DNA tools, molecular cloning, microscopy, membrane structure, and membrane transport. Give clear, concise answers suitable for an undergrad exam. Use bullet points and bold key terms. Keep answers under 200 words.",
          messages: [{ role: "user", content: aiQuestion }]
        })
      });
      const data = await res.json();
      setAiAnswer(data.content?.[0]?.text || "No response.");
    } catch {
      setAiAnswer("Error connecting to AI. Please try again.");
    }
    setAiLoading(false);
  };

  const nextCard = () => { setCardIndex((i) => (i + 1) % cards.length); setFlipped(false); };
  const prevCard = () => { setCardIndex((i) => (i - 1 + cards.length) % cards.length); setFlipped(false); };
  const markMastered = () => {
    setMasteredCards(m => ({ ...m, [`${activeTopic}-${cardIndex}`]: !m[`${activeTopic}-${cardIndex}`] }));
  };

  const handleQuizAnswer = (i) => {
    if (selected !== null) return;
    setSelected(i);
    if (i === QUIZ_QUESTIONS[quizIndex].answer) setQuizScore(s => s + 1);
  };
  const nextQuestion = () => {
    if (quizIndex + 1 >= QUIZ_QUESTIONS.length) { setQuizDone(true); return; }
    setQuizIndex(q => q + 1);
    setSelected(null);
  };
  const resetQuiz = () => { setQuizIndex(0); setSelected(null); setQuizScore(0); setQuizDone(false); };

  const totalCards = Object.values(TOPICS).reduce((s, t) => s + t.flashcards.length, 0);
  const masteredCount = Object.values(masteredCards).filter(Boolean).length;

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0f",
      color: "#e8e8f0",
      fontFamily: "'Georgia', 'Times New Roman', serif",
      position: "relative",
      overflow: "hidden"
    }}>
      {/* Background grid */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 0,
        backgroundImage: "linear-gradient(rgba(0,229,204,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,204,0.03) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
        pointerEvents: "none"
      }} />

      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 50,
        background: "rgba(10,10,15,0.95)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(0,229,204,0.15)",
        padding: "16px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: "linear-gradient(135deg, #00E5CC, #A78BFA)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18
          }}>🧬</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "0.05em", color: "#00E5CC" }}>CELL BIO</div>
            <div style={{ fontSize: 11, color: "#666", letterSpacing: "0.1em" }}>STUDY SYSTEM</div>
          </div>
        </div>
        <nav style={{ display: "flex", gap: 4 }}>
          {[["home","Home"],["flashcards","Flashcards"],["quiz","Quiz"],["deeper","Deep Dive"],["ai","Ask AI"]].map(([id, label]) => (
            <button key={id} onClick={() => { setActiveTab(id); if(id !== "flashcards") setActiveTopic(null); }}
              style={{
                padding: "7px 14px", borderRadius: 6, fontSize: 13, border: "none", cursor: "pointer",
                background: activeTab === id ? "rgba(0,229,204,0.15)" : "transparent",
                color: activeTab === id ? "#00E5CC" : "#888",
                letterSpacing: "0.03em", fontFamily: "inherit",
                transition: "all 0.2s"
              }}>
              {label}
            </button>
          ))}
        </nav>
        <div style={{ fontSize: 12, color: "#555" }}>
          {masteredCount}/{totalCards} <span style={{ color: "#00E5CC" }}>mastered</span>
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 1, maxWidth: 900, margin: "0 auto", padding: "32px 24px" }}>

        {/* HOME */}
        {activeTab === "home" && (
          <div>
            <div style={{ marginBottom: 40, textAlign: "center" }}>
              <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "#00E5CC", marginBottom: 12 }}>LECTURES 08–13</div>
              <h1 style={{ fontSize: 42, fontWeight: 300, margin: "0 0 12px", lineHeight: 1.2 }}>
                Cell Biology<br /><span style={{ fontWeight: 700, fontStyle: "italic" }}>Study Guide</span>
              </h1>
              <p style={{ color: "#666", fontSize: 15, maxWidth: 500, margin: "0 auto" }}>
                Flashcards, quizzes, deep dives, and an AI tutor — all from your lecture notes.
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
              {Object.entries(TOPICS).map(([key, t]) => {
                const topicMastered = t.flashcards.filter((_, i) => masteredCards[`${key}-${i}`]).length;
                return (
                  <div key={key}
                    onClick={() => { setActiveTab("flashcards"); setActiveTopic(key); setCardIndex(0); setFlipped(false); }}
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: `1px solid rgba(255,255,255,0.08)`,
                      borderRadius: 12, padding: 20, cursor: "pointer",
                      transition: "all 0.2s",
                      position: "relative", overflow: "hidden"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = t.color + "66"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                  >
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${t.color}, transparent)` }} />
                    <div style={{ fontSize: 28, marginBottom: 10 }}>{t.icon}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: "#e8e8f0" }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: "#555", marginBottom: 12 }}>{t.flashcards.length} flashcards</div>
                    <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 99 }}>
                      <div style={{ height: "100%", width: `${(topicMastered / t.flashcards.length) * 100}%`, background: t.color, borderRadius: 99, transition: "width 0.5s" }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#444", marginTop: 6 }}>{topicMastered}/{t.flashcards.length} mastered</div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 32, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              {[
                { label: "Total Flashcards", val: totalCards, color: "#00E5CC" },
                { label: "Quiz Questions", val: QUIZ_QUESTIONS.length, color: "#A78BFA" },
                { label: "Topics Covered", val: Object.keys(TOPICS).length, color: "#F472B6" },
              ].map(s => (
                <div key={s.label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 20, textAlign: "center" }}>
                  <div style={{ fontSize: 36, fontWeight: 700, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: 12, color: "#555", letterSpacing: "0.05em" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FLASHCARDS */}
        {activeTab === "flashcards" && (
          <div>
            {!activeTopic ? (
              <div>
                <h2 style={{ fontSize: 28, fontWeight: 300, marginBottom: 8 }}>Choose a <em>Topic</em></h2>
                <p style={{ color: "#555", marginBottom: 28 }}>Select a topic to start reviewing flashcards.</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                  {Object.entries(TOPICS).map(([key, t]) => (
                    <button key={key} onClick={() => { setActiveTopic(key); setCardIndex(0); setFlipped(false); }}
                      style={{
                        background: "rgba(255,255,255,0.03)", border: `1px solid rgba(255,255,255,0.08)`,
                        borderRadius: 10, padding: "16px", cursor: "pointer", textAlign: "left",
                        color: "#e8e8f0", fontFamily: "inherit", transition: "all 0.2s"
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = t.color + "88"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"}
                    >
                      <div style={{ fontSize: 24, marginBottom: 8 }}>{t.icon}</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>{t.flashcards.length} cards</div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
                  <button onClick={() => { setActiveTopic(null); setFlipped(false); }}
                    style={{ background: "none", border: "1px solid rgba(255,255,255,0.1)", color: "#888", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
                    ← Back
                  </button>
                  <div style={{ fontSize: 22, }}>{topic.icon}</div>
                  <h2 style={{ fontSize: 24, fontWeight: 300, margin: 0 }}>{topic.label}</h2>
                  <div style={{ marginLeft: "auto", fontSize: 13, color: "#555" }}>{cardIndex + 1} / {cards.length}</div>
                </div>

                {/* Progress */}
                <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 99, marginBottom: 28 }}>
                  <div style={{ height: "100%", width: `${((cardIndex + 1) / cards.length) * 100}%`, background: topic.color, borderRadius: 99, transition: "width 0.3s" }} />
                </div>

                {/* Card */}
                <div onClick={() => setFlipped(f => !f)}
                  style={{
                    background: flipped ? `rgba(${topic.color === "#00E5CC" ? "0,229,204" : "167,139,250"},0.08)` : "rgba(255,255,255,0.04)",
                    border: `1px solid ${flipped ? topic.color + "44" : "rgba(255,255,255,0.08)"}`,
                    borderRadius: 16, padding: "48px 40px", cursor: "pointer",
                    minHeight: 220, display: "flex", flexDirection: "column", justifyContent: "center",
                    transition: "all 0.3s", position: "relative", textAlign: "center"
                  }}>
                  <div style={{ position: "absolute", top: 16, left: 20, fontSize: 11, letterSpacing: "0.2em", color: flipped ? topic.color : "#444" }}>
                    {flipped ? "ANSWER" : "QUESTION"}
                  </div>
                  {masteredCards[`${activeTopic}-${cardIndex}`] && (
                    <div style={{ position: "absolute", top: 12, right: 16, fontSize: 18 }}>✅</div>
                  )}
                  <div style={{ fontSize: flipped ? 15 : 18, lineHeight: 1.7, whiteSpace: "pre-line", color: flipped ? "#e8e8f0" : "#f0f0ff" }}>
                    {flipped ? cards[cardIndex].a : cards[cardIndex].q}
                  </div>
                  {!flipped && <div style={{ marginTop: 20, fontSize: 12, color: "#444" }}>Click to reveal answer</div>}
                </div>

                <div style={{ display: "flex", gap: 12, marginTop: 20, justifyContent: "center", alignItems: "center" }}>
                  <button onClick={prevCard} style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "none", color: "#888", cursor: "pointer", fontFamily: "inherit", fontSize: 14 }}>← Prev</button>
                  <button onClick={markMastered}
                    style={{
                      padding: "10px 20px", borderRadius: 8, border: `1px solid ${masteredCards[`${activeTopic}-${cardIndex}`] ? topic.color + "88" : "rgba(255,255,255,0.1)"}`,
                      background: masteredCards[`${activeTopic}-${cardIndex}`] ? `rgba(0,229,204,0.1)` : "none",
                      color: masteredCards[`${activeTopic}-${cardIndex}`] ? topic.color : "#555",
                      cursor: "pointer", fontFamily: "inherit", fontSize: 13
                    }}>
                    {masteredCards[`${activeTopic}-${cardIndex}`] ? "✓ Mastered" : "Mark Mastered"}
                  </button>
                  <button onClick={nextCard} style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "none", color: "#888", cursor: "pointer", fontFamily: "inherit", fontSize: 14 }}>Next →</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* QUIZ */}
        {activeTab === "quiz" && (
          <div>
            <h2 style={{ fontSize: 28, fontWeight: 300, marginBottom: 4 }}>Practice <em>Quiz</em></h2>
            <p style={{ color: "#555", marginBottom: 28 }}>{QUIZ_QUESTIONS.length} questions from your lecture material.</p>

            {quizDone ? (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <div style={{ fontSize: 72, fontWeight: 700, color: quizScore >= 8 ? "#34D399" : quizScore >= 6 ? "#FBBF24" : "#FF6B6B" }}>
                  {quizScore}/{QUIZ_QUESTIONS.length}
                </div>
                <div style={{ fontSize: 18, color: "#888", margin: "12px 0 8px" }}>
                  {quizScore >= 8 ? "🎉 Excellent work!" : quizScore >= 6 ? "👍 Good effort!" : "📖 Keep studying!"}
                </div>
                <div style={{ fontSize: 14, color: "#555", marginBottom: 28 }}>{Math.round((quizScore / QUIZ_QUESTIONS.length) * 100)}% correct</div>
                <button onClick={resetQuiz} style={{ padding: "12px 32px", borderRadius: 8, border: "1px solid #00E5CC44", background: "rgba(0,229,204,0.1)", color: "#00E5CC", cursor: "pointer", fontFamily: "inherit", fontSize: 15 }}>
                  Try Again
                </button>
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24, fontSize: 13, color: "#555" }}>
                  <span>Question {quizIndex + 1} of {QUIZ_QUESTIONS.length}</span>
                  <span style={{ color: "#34D399" }}>Score: {quizScore}/{quizIndex}</span>
                </div>
                <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 99, marginBottom: 28 }}>
                  <div style={{ height: "100%", width: `${((quizIndex) / QUIZ_QUESTIONS.length) * 100}%`, background: "#A78BFA", borderRadius: 99 }} />
                </div>

                <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "28px 28px 24px" }}>
                  <div style={{ fontSize: 17, lineHeight: 1.6, marginBottom: 24, color: "#f0f0ff" }}>
                    {QUIZ_QUESTIONS[quizIndex].q}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {QUIZ_QUESTIONS[quizIndex].options.map((opt, i) => {
                      const isCorrect = i === QUIZ_QUESTIONS[quizIndex].answer;
                      const isSelected = i === selected;
                      let bg = "rgba(255,255,255,0.03)";
                      let borderColor = "rgba(255,255,255,0.08)";
                      let color = "#ccc";
                      if (selected !== null) {
                        if (isCorrect) { bg = "rgba(52,211,153,0.12)"; borderColor = "#34D39966"; color = "#34D399"; }
                        else if (isSelected) { bg = "rgba(255,107,107,0.12)"; borderColor = "#FF6B6B66"; color = "#FF6B6B"; }
                      }
                      return (
                        <button key={i} onClick={() => handleQuizAnswer(i)}
                          style={{
                            background: bg, border: `1px solid ${borderColor}`, borderRadius: 8,
                            padding: "12px 16px", textAlign: "left", cursor: selected ? "default" : "pointer",
                            color, fontFamily: "inherit", fontSize: 14, lineHeight: 1.5, transition: "all 0.2s"
                          }}>
                          <span style={{ opacity: 0.5, marginRight: 8 }}>{String.fromCharCode(65 + i)}.</span>{opt}
                        </button>
                      );
                    })}
                  </div>

                  {selected !== null && (
                    <div style={{ marginTop: 20, padding: "14px 16px", background: "rgba(255,255,255,0.04)", borderRadius: 8, borderLeft: `3px solid ${selected === QUIZ_QUESTIONS[quizIndex].answer ? "#34D399" : "#FF6B6B"}` }}>
                      <div style={{ fontSize: 12, letterSpacing: "0.1em", color: "#555", marginBottom: 4 }}>EXPLANATION</div>
                      <div style={{ fontSize: 13, color: "#ccc", lineHeight: 1.6 }}>{QUIZ_QUESTIONS[quizIndex].explain}</div>
                    </div>
                  )}

                  {selected !== null && (
                    <button onClick={nextQuestion}
                      style={{ marginTop: 16, padding: "10px 24px", borderRadius: 8, border: "1px solid #A78BFA44", background: "rgba(167,139,250,0.1)", color: "#A78BFA", cursor: "pointer", fontFamily: "inherit", fontSize: 14 }}>
                      {quizIndex + 1 >= QUIZ_QUESTIONS.length ? "See Results →" : "Next Question →"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* DEEPER */}
        {activeTab === "deeper" && (
          <div>
            <h2 style={{ fontSize: 28, fontWeight: 300, marginBottom: 4 }}>Deep <em>Dives</em></h2>
            <p style={{ color: "#555", marginBottom: 28 }}>Go beyond the lecture notes — exam-level depth on every topic.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 }}>
              {Object.entries(TOPICS).map(([key, t]) => (
                <button key={key} onClick={() => setDeepTopic(deepTopic === key ? null : key)}
                  style={{
                    background: deepTopic === key ? `rgba(0,0,0,0.4)` : "rgba(255,255,255,0.03)",
                    border: `1px solid ${deepTopic === key ? t.color + "88" : "rgba(255,255,255,0.08)"}`,
                    borderRadius: 10, padding: "14px 16px", textAlign: "left", cursor: "pointer",
                    color: deepTopic === key ? t.color : "#ccc", fontFamily: "inherit", transition: "all 0.2s"
                  }}>
                  <span style={{ marginRight: 8 }}>{t.icon}</span>
                  <span style={{ fontSize: 14 }}>{t.label}</span>
                  <span style={{ float: "right", opacity: 0.4 }}>{deepTopic === key ? "▲" : "▼"}</span>
                </button>
              ))}
            </div>
            {deepTopic && (
              <div style={{
                background: "rgba(255,255,255,0.04)", border: `1px solid ${TOPICS[deepTopic].color}33`,
                borderRadius: 14, padding: "28px",
                borderLeft: `3px solid ${TOPICS[deepTopic].color}`
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 24 }}>{TOPICS[deepTopic].icon}</span>
                  <h3 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: TOPICS[deepTopic].color }}>{TOPICS[deepTopic].label}</h3>
                </div>
                <div style={{ fontSize: 15, lineHeight: 1.8, color: "#ccc", whiteSpace: "pre-wrap" }}>
                  {TOPICS[deepTopic].deeper}
                </div>
              </div>
            )}
            {!deepTopic && (
              <div style={{ textAlign: "center", padding: "40px", color: "#444" }}>
                Select a topic above to read the deep dive
              </div>
            )}
          </div>
        )}

        {/* ASK AI */}
        {activeTab === "ai" && (
          <div>
            <h2 style={{ fontSize: 28, fontWeight: 300, marginBottom: 4 }}>Ask the <em>AI Tutor</em></h2>
            <p style={{ color: "#555", marginBottom: 28 }}>Ask any question about the lecture material and get a clear explanation.</p>

            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              <input
                value={aiQuestion}
                onChange={e => setAiQuestion(e.target.value)}
                onKeyDown={e => e.key === "Enter" && askAI()}
                placeholder="e.g. What is the difference between miRNA and siRNA?"
                style={{
                  flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8, padding: "12px 16px", color: "#e8e8f0", fontFamily: "inherit", fontSize: 14,
                  outline: "none"
                }}
              />
              <button onClick={askAI} disabled={aiLoading}
                style={{
                  padding: "12px 24px", borderRadius: 8, border: "none",
                  background: "linear-gradient(135deg, #00E5CC, #A78BFA)",
                  color: "#0a0a0f", cursor: aiLoading ? "wait" : "pointer",
                  fontFamily: "inherit", fontSize: 14, fontWeight: 700, minWidth: 80
                }}>
                {aiLoading ? "..." : "Ask →"}
              </button>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
              {[
                "Explain the CRISPR-Cas9 mechanism",
                "How does the Na⁺-K⁺ pump work?",
                "What are the types of membrane proteins?",
                "How does PCR amplify DNA?",
                "What is RNAi and how does it work?",
              ].map(s => (
                <button key={s} onClick={() => setAiQuestion(s)}
                  style={{
                    padding: "6px 12px", borderRadius: 99, border: "1px solid rgba(255,255,255,0.1)",
                    background: "none", color: "#666", cursor: "pointer", fontFamily: "inherit", fontSize: 12
                  }}>
                  {s}
                </button>
              ))}
            </div>

            {aiAnswer && (
              <div style={{
                background: "rgba(255,255,255,0.04)", border: "1px solid rgba(0,229,204,0.2)",
                borderRadius: 14, padding: "24px 24px",
                borderLeft: "3px solid #00E5CC"
              }}>
                <div style={{ fontSize: 11, letterSpacing: "0.15em", color: "#00E5CC", marginBottom: 12 }}>AI TUTOR RESPONSE</div>
                <div style={{ fontSize: 15, lineHeight: 1.8, color: "#ddd", whiteSpace: "pre-wrap" }}>{aiAnswer}</div>
              </div>
            )}
            {aiLoading && (
              <div style={{ textAlign: "center", padding: 40, color: "#444", fontSize: 14 }}>
                Thinking<span style={{ animation: "pulse 1s infinite" }}>...</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}